import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { fmtNumber } from '../components/Staffing/format';
import { c, t, sp, card, btn, tbl } from '../theme';

// Manual fallback for the daily Vacancies feed (docs/staffing-vacancies-ingest.md
// / staffingVacancyFtpWatcher.js) — the standalone Workday "Job Requisitions"
// report, not the combined Roster+Vacancies workbook (that's Staffing Tracker
// Upload). No preview step: this commits straight away, the same way the
// automated Job will once it exists, so testing here is testing the real path.
export default function StaffingJobRequisitionsUpload() {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ['staffing-batches'],
    queryFn:  () => api.get('/staffing/batches'),
    refetchInterval: 15000,
  });
  const vacancyBatches = batches.filter((b) => b.kind === 'vacancy');

  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload('/staffing/job-requisitions/upload', fd, setProgress);
    },
    onSuccess: (data) => {
      toast.success(
        `Committed ${fmtNumber(data.vacancyRowCount)} vacancy rows`
        + (data.unmappedRowCount ? ` (${fmtNumber(data.unmappedRowCount)} unmapped).` : '.')
      );
      setFile(null); setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
      qc.invalidateQueries({ queryKey: ['staffing-batches'] });
      qc.invalidateQueries({ queryKey: ['staffing-summary'] });
      qc.invalidateQueries({ queryKey: ['staffing-vacancies'] });
    },
    onError: (err) => { toast.error(err.message || 'Upload failed.'); setProgress(0); },
  });

  const busy = uploadMutation.isPending;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Upload Job Requisitions"
        subtitle="The standalone daily Workday report Vacancies will refresh from automatically (/Staffing_Tracker on SFTP) — not the combined workbook. Uploading here replaces the current Vacancies snapshot immediately; it never touches Roster."
      />

      <div style={s.card}>
        <div
          style={{ ...s.dropZone, borderColor: file ? '#86EFAC' : c.borderStrong }}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
        >
          <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
                 onChange={(e) => setFile(e.target.files[0] || null)} />
          <UploadCloud size={26} color={file ? c.success : c.textFaint} />
          <div style={s.dropText}>
            {file ? (
              <><strong style={{ color: c.success }}>{file.name}</strong><br /><span style={{ color: c.textFaint }}>{(file.size / 1024).toFixed(0)} KB</span></>
            ) : (
              <><strong>Click to select</strong> or drag &amp; drop<br /><span style={{ color: c.textFaint }}>Job Requisitions .csv, .xlsx or .xls</span></>
            )}
          </div>
        </div>

        {uploadMutation.isPending && (
          <div style={s.progressWrap}>
            <div style={s.progressTrack}><div style={{ ...s.progressFill, width: `${progress}%` }} /></div>
            <span style={s.progressLabel}>{progress}%</span>
          </div>
        )}

        <button
          style={{ ...btn.primary, opacity: file && !busy ? 1 : 0.5 }}
          disabled={!file || busy}
          onClick={() => uploadMutation.mutate()}
        >
          <CheckCircle size={15} /> {uploadMutation.isPending ? 'Uploading…' : 'Upload & Commit'}
        </button>
      </div>

      <div style={s.logSection}>
        <div style={s.logTitle}>Recent Vacancy Imports</div>
        {batchesLoading ? <LoadingSpinner /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.logTable}>
              <thead>
                <tr>{['File', 'Source', 'Status', 'Current', 'Vacancy Rows', 'By', 'When'].map((h) => <th key={h} style={tbl.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {vacancyBatches.map((b) => {
                  const st = STATUS_STYLE[b.status] || STATUS_STYLE.preview;
                  return (
                    <tr key={b.id} style={tbl.tr}>
                      <td style={tbl.td} title={b.file_name}>{b.file_name?.slice(-40)}</td>
                      <td style={tbl.td}>{b.source === 'ftp' ? '⟳ FTP' : '↑ Manual'}</td>
                      <td style={tbl.td}><span style={{ ...s.statusPill, color: st.color, background: st.bg }}>{st.icon} {b.status}</span></td>
                      <td style={tbl.td}>{b.is_current_vacancy ? '✓' : ''}</td>
                      <td style={tbl.tdR}>{fmtNumber(b.vacancy_row_count)}</td>
                      <td style={tbl.td}>{b.uploaded_by_name || (b.source === 'ftp' ? 'System' : '—')}</td>
                      <td style={tbl.td}>{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                    </tr>
                  );
                })}
                {!vacancyBatches.length && <tr><td colSpan={7} style={s.emptyLog}>No Job Requisitions imports yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  committed: { color: c.successText, bg: c.successTint, icon: <CheckCircle size={12} /> },
  preview:   { color: c.info,        bg: c.infoTint,    icon: <AlertTriangle size={12} /> },
  cancelled: { color: c.textFaint,   bg: c.canvas,      icon: <XCircle size={12} /> },
  failed:    { color: c.dangerText,  bg: c.dangerTint,  icon: <XCircle size={12} /> },
};

const s = {
  page:        { maxWidth: '1000px', margin: '0 auto' },
  card:        { ...card.sm, padding: '16px', marginBottom: sp[5] },
  dropZone:    { borderWidth: '2px', borderStyle: 'dashed', borderRadius: '10px', padding: '30px', textAlign: 'center', cursor: 'pointer', marginBottom: sp[4], display: 'flex', flexDirection: 'column', alignItems: 'center', gap: sp[2] },
  dropText:    { fontSize: t.bodySm.fontSize, color: c.textStrong, lineHeight: 1.6 },
  progressWrap:{ display: 'flex', alignItems: 'center', gap: sp[2], marginBottom: sp[4] },
  progressTrack:{ flex: 1, height: '5px', background: c.border, borderRadius: '4px', overflow: 'hidden' },
  progressFill:{ height: '100%', background: c.navy, borderRadius: '4px', transition: 'width 0.2s' },
  progressLabel:{ fontSize: t.caption.fontSize, fontWeight: 600, color: c.textStrong, minWidth: '30px' },
  logSection:  { ...card.sm, overflow: 'hidden' },
  logTitle:    { ...card.head },
  logTable:    { width: '100%', borderCollapse: 'collapse', fontSize: t.bodySm.fontSize },
  statusPill:  { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: t.caption.fontSize, fontWeight: 500 },
  emptyLog:    { textAlign: 'center', padding: '24px', color: c.textFaint },
};
